import React, { useEffect, useState } from "react";
import { apiGet, apiPost, formatDateTime } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ExternalLink, Newspaper, RefreshCcw, TrendingUp, TrendingDown, Minus, Loader2, Landmark } from "lucide-react";
import { toast } from "sonner";

const SENTIMENT_META = {
  positive: {
    label: "Positive",
    Icon: TrendingUp,
    badge:
      "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30",
    bar: "bg-[hsl(var(--success))]",
  },
  neutral: {
    label: "Neutral",
    Icon: Minus,
    badge: "bg-muted text-muted-foreground border-border",
    bar: "bg-muted-foreground",
  },
  negative: {
    label: "Negative",
    Icon: TrendingDown,
    badge:
      "bg-[hsl(var(--destructive))]/15 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/30",
    bar: "bg-[hsl(var(--destructive))]",
  },
};

const NewsTab = ({ companyCode }) => {
  const { isAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [collecting, setCollecting] = useState(false);
  const [collectingAll, setCollectingAll] = useState(false);
  const [lastCollectionAt, setLastCollectionAt] = useState(null);

  const load = () => {
    setLoading(true);
    apiGet(`/news/${companyCode}?limit=20`)
      .then((d) => {
        setData(d);
        if (d?.last_collection_at) setLastCollectionAt(d.last_collection_at);
      })
      .catch((e) => setError(e.message || "Failed to load news"))
      .finally(() => setLoading(false));
  };

  const collectNow = async () => {
    setCollecting(true);
    try {
      const res = await apiPost(`/admin/news/refresh-company/${companyCode}`);
      const matched = res?.scored_pairs ?? res?.matched_articles ?? 0;
      setLastCollectionAt(new Date().toISOString());
      toast.success(`News collection complete`, {
        description: matched > 0
          ? `${matched} new article${matched === 1 ? "" : "s"} found for ${companyCode}.`
          : `No new articles for ${companyCode} in this run.`,
      });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "News collection failed");
    } finally {
      setCollecting(false);
    }
  };

  const collectAll = async () => {
    if (!window.confirm("Run news collection for ALL companies? This may take 1-2 minutes.")) return;
    setCollectingAll(true);
    try {
      const res = await apiPost(`/admin/news/refresh-all`);
      const total = res?.new_articles ?? res?.matched_articles ?? 0;
      toast.success(`All-stocks collection complete`, {
        description: `${total} new article${total === 1 ? "" : "s"} indexed across the universe.`,
      });
      setLastCollectionAt(new Date().toISOString());
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Collect All failed");
    } finally {
      setCollectingAll(false);
    }
  };

  useEffect(() => {
    if (!companyCode) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyCode]);

  if (loading) {
    return (
      <Card className="border bg-card" data-testid="news-tab-loading">
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          Loading news…
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border bg-card">
        <CardContent className="py-10 text-center text-sm text-[hsl(var(--destructive))]">
          {error}
        </CardContent>
      </Card>
    );
  }

  const items = data?.items || [];
  const summary = data?.summary || { total: 0, counts: {} };
  const counts = summary.counts || {};
  const total = summary.total || 0;

  return (
    <div className="space-y-4" data-testid="news-tab">
      <Card className="border bg-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <CardTitle className="font-display flex items-center gap-2 text-base">
              <Newspaper className="h-4 w-4 text-primary" />
              Sentiment overview
            </CardTitle>
            <div className="flex items-center gap-2">
              {isAdmin && (
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1.5"
                  onClick={collectNow}
                  disabled={collecting}
                  data-testid="news-collect-now-button"
                  title="Manually trigger FT.lk RSS scrape for this company"
                >
                  {collecting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Newspaper className="h-3.5 w-3.5" />}
                  Collect News Now
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                className="gap-1.5"
                onClick={load}
                data-testid="news-reload-button"
              >
                <RefreshCcw className="h-3.5 w-3.5" />
                Reload
              </Button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            {total === 0
              ? `No news ingested yet for ${companyCode}.`
              : `Across ${total} article${total === 1 ? "" : "s"} sourced from Sri Lankan business media`}
          </p>
          {data?.last_refresh && (
            <p className="text-[11px] text-muted-foreground/80 mt-0.5" data-testid="news-last-refresh">
              Last collection run: {formatDateTime(data.last_refresh)}
            </p>
          )}
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-3">
            {["positive", "neutral", "negative"].map((k) => {
              const m = SENTIMENT_META[k];
              const n = counts[k] || 0;
              const pct = total > 0 ? Math.round((n / total) * 100) : 0;
              return (
                <div
                  key={k}
                  className="rounded-lg border bg-background p-3"
                  data-testid={`news-summary-${k}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs uppercase tracking-wide text-muted-foreground">
                      {m.label}
                    </span>
                    <m.Icon className="h-3.5 w-3.5 text-muted-foreground" />
                  </div>
                  <div className="mt-1 flex items-baseline gap-2">
                    <span className="text-2xl font-display font-semibold tabular">{n}</span>
                    <span className="text-xs text-muted-foreground tabular">{pct}%</span>
                  </div>
                  <div className="mt-2 h-1 rounded-full bg-muted overflow-hidden">
                    <div
                      className={`h-full ${m.bar} transition-all`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {items.length === 0 ? (
        <Card className="border bg-card">
          <CardContent className="py-10 text-center" data-testid="news-empty-state">
            <Newspaper className="h-7 w-7 mx-auto mb-3 text-muted-foreground/60" />
            <p className="text-sm font-medium">No articles for {companyCode} yet</p>
            <p className="mt-1.5 text-xs text-muted-foreground max-w-md mx-auto leading-relaxed">
              News is collected automatically Monday to Friday at 7:00am Sri Lanka time
              from FT.lk and other Sri Lankan business media.
              <br />
              Check back Monday morning for the latest articles about {companyCode}.
              {lastCollectionAt && (
                <>
                  <br />
                  <span className="text-[11px] opacity-80">
                    Last collection attempt: {new Date(lastCollectionAt).toLocaleString()} — no new articles found
                  </span>
                </>
              )}
            </p>
            {isAdmin && (
              <div className="mt-4 flex flex-col sm:flex-row gap-2 justify-center">
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1.5"
                  onClick={collectNow}
                  disabled={collecting}
                  data-testid="news-empty-collect-button"
                >
                  {collecting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Newspaper className="h-3.5 w-3.5" />}
                  Collect News for {companyCode}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="gap-1.5"
                  onClick={collectAll}
                  disabled={collectingAll}
                  data-testid="news-empty-collect-all-button"
                  title="Run the news collector for ALL companies"
                >
                  {collectingAll ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Newspaper className="h-3.5 w-3.5" />}
                  Collect All Stocks
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="news-list">
          {items.map((it) => (
            <NewsItem key={it.article_id} item={it} />
          ))}
        </div>
      )}
    </div>
  );
};

const NewsItem = ({ item }) => {
  const meta = SENTIMENT_META[item.sentiment] || SENTIMENT_META.neutral;
  // Phase 7 Stage 4 — surface CSE primary-source rows + low-relevance rows
  const isCseAnnouncement = item.source === "cse_announcement";
  const isLowRelevance = item.relevance != null && item.relevance <= 60 && !isCseAnnouncement;
  // CSE rows synthesise a non-clickable url (cse://...). Don't link these.
  const linkable = item.url && !item.url.startsWith("cse://");
  const Wrapper = linkable ? "a" : "div";
  const wrapperProps = linkable
    ? { href: item.url, target: "_blank", rel: "noreferrer noopener" }
    : {};
  return (
    <Wrapper
      {...wrapperProps}
      className={`block rounded-lg border bg-background ${linkable ? "hover:bg-muted/40 transition-colors duration-150" : ""} p-4 ${isLowRelevance ? "opacity-80" : ""}`}
      data-testid={`news-item-${item.article_id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            {isCseAnnouncement && (
              <Badge
                variant="outline"
                className="text-[10px] gap-1 bg-primary/10 text-primary border-primary/30"
                data-testid={`news-cse-badge-${item.article_id}`}
              >
                <Landmark className="h-3 w-3" />
                CSE
              </Badge>
            )}
            <Badge
              variant="outline"
              className={`${meta.badge} text-[10px] gap-1`}
              data-testid={`news-sentiment-${item.article_id}`}
            >
              <meta.Icon className="h-3 w-3" />
              {meta.label}
            </Badge>
            {isLowRelevance && (
              <Badge
                variant="outline"
                className="text-[10px] bg-muted/60 text-muted-foreground border"
                data-testid={`news-low-relevance-${item.article_id}`}
                title="Excluded from Layer 5 sentiment scoring (relevance ≤ 60)"
              >
                Low relevance
              </Badge>
            )}
            {item.category && isCseAnnouncement && (
              <Badge
                variant="outline"
                className="text-[10px] bg-card text-foreground/80 border"
                data-testid={`news-category-${item.article_id}`}
              >
                {item.category}
              </Badge>
            )}
            {item.relevance != null && (
              <span className="text-[11px] text-muted-foreground tabular">
                Relevance {item.relevance}%
              </span>
            )}
            {item.confidence != null && !isCseAnnouncement && (
              <span className="text-[11px] text-muted-foreground tabular">
                · Confidence {Math.round((item.confidence || 0) * 100)}%
              </span>
            )}
          </div>
          <h3 className="mt-1.5 text-sm font-medium leading-snug group-hover:text-primary">
            {item.title}
          </h3>
          {item.summary && (
            <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{item.summary}</p>
          )}
          {item.rationale && (
            <p className="mt-2 text-xs text-foreground/80 italic">
              <span className="text-muted-foreground not-italic">{isCseAnnouncement ? "Filing: " : "AI take: "}</span>
              {item.rationale}
            </p>
          )}
          <div className="mt-2 flex items-center gap-2 text-[11px] text-muted-foreground">
            <span className="font-mono">{item.domain || item.source_name}</span>
            {item.published_at && <span>· {formatDateTime(item.published_at)}</span>}
          </div>
        </div>
        {linkable && <ExternalLink className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />}
      </div>
    </Wrapper>
  );
};

export default NewsTab;
